import React, { useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import PhotoUploader from "./PhotoUploader";
import { X } from "lucide-react";

export default function InspectionDialog({ open, deviceId, initial, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    inspection_date: today,
    expiration_months: 12,
    result: "superato",
    notes: "",
    signature: "",
    photos: [],
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        inspection_date: initial.inspection_date,
        expiration_months: initial.expiration_months,
        result: initial.result,
        notes: initial.notes || "",
        signature: initial.signature || "",
        photos: initial.photos || [],
      });
    } else {
      setForm({ inspection_date: today, expiration_months: 12, result: "superato", notes: "", signature: "", photos: [] });
    }
    setError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial]);

  if (!open) return null;

  const isEdit = Boolean(initial);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const payload = { ...form, expiration_months: Number(form.expiration_months) };
      let data;
      if (isEdit) {
        ({ data } = await api.patch(`/inspections/${initial.id}`, payload));
      } else {
        ({ data } = await api.post(`/devices/${deviceId}/inspections`, payload));
      }
      setLoading(false);
      onSaved(data);
    } catch (e) {
      setLoading(false);
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 sm:p-8 overflow-y-auto"
      data-testid="inspection-dialog"
      onClick={onClose}
    >
      <div className="bru-card w-full max-w-xl p-6 mt-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-5">
          <div>
            <div className="tag-label">{isEdit ? "MODIFICA" : "REGISTRA"} · ISPEZIONE</div>
            <h3 className="text-2xl font-bold" >
              {isEdit ? "Modifica Ispezione" : "Nuova Ispezione"}
            </h3>
          </div>
          <button onClick={onClose} className="bru-btn bru-btn-ghost !px-2 !py-1" data-testid="close-inspection-dialog">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="bru-label">Data Ispezione *</label>
              <input
                type="date"
                required
                min="1990-01-01"
                max="2099-12-31"
                value={form.inspection_date}
                onChange={(e) => update("inspection_date", e.target.value)}
                className="bru-input"
                data-testid="inspection-date-input"
              />
            </div>
            <div>
              <label className="bru-label">Validità *</label>
              <select
                value={form.expiration_months}
                onChange={(e) => update("expiration_months", e.target.value)}
                className="bru-input"
                data-testid="inspection-months-select"
              >
                <option value={1}>1 mese</option>
                <option value={6}>6 mesi</option>
                <option value={12}>12 mesi</option>
                <option value={24}>24 mesi</option>
              </select>
            </div>
          </div>
          <div>
            <label className="bru-label">Esito *</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => update("result", "superato")}
                className={`px-3 py-3 border-[1.5px] border-black font-bold uppercase tracking-wider text-sm ${
                  form.result === "superato" ? "bg-[#10b981] text-white bru-shadow-sm" : "bg-white hover:bg-neutral-50"
                }`}
                data-testid="inspection-result-superato"
              >
                ✓ Superato
              </button>
              <button
                type="button"
                onClick={() => update("result", "fallito")}
                className={`px-3 py-3 border-[1.5px] border-black font-bold uppercase tracking-wider text-sm ${
                  form.result === "fallito" ? "bg-[#ff3b30] text-white bru-shadow-sm" : "bg-white hover:bg-neutral-50"
                }`}
                data-testid="inspection-result-fallito"
              >
                ✗ Fallito
              </button>
            </div>
          </div>
          <div>
            <label className="bru-label">Note Dettagliate</label>
            <textarea
              rows={4}
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
              className="bru-input"
              placeholder="Dettagli della verifica, difetti riscontrati, interventi..."
              data-testid="inspection-notes-input"
            />
          </div>
          <div>
            <label className="bru-label">Firma *</label>
            <input
              required
              value={form.signature}
              onChange={(e) => update("signature", e.target.value)}
              className="bru-input"
              style={{ fontFamily: "'Brush Script MT', cursive", fontSize: "20px" }}
              placeholder="Firma ispettore"
              data-testid="inspection-signature-input"
            />
          </div>
          <div>
            <label className="bru-label">Foto ispezione (max 3)</label>
            <PhotoUploader value={form.photos} onChange={(p) => update("photos", p)} max={3} />
          </div>
          {error && (
            <div className="border-[1.5px] border-black bg-[#ff3b30] text-white px-3 py-2 text-sm" data-testid="inspection-error">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="bru-btn bru-btn-secondary" data-testid="cancel-inspection-button">
              Annulla
            </button>
            <button type="submit" disabled={loading} className="bru-btn bru-btn-primary disabled:opacity-60" data-testid="submit-inspection-button">
              {loading ? "SALVATAGGIO…" : isEdit ? "AGGIORNA ISPEZIONE" : "SALVA ISPEZIONE"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
